import {
  DomainFrequencyType,
  DomainIncomeType,
  type DtoCreateIncomeReq,
  type DtoGetBorrowerIncomesResp,
  type DtoIncome,
  type DtoUpdateIncomeReq,
  type DomainIncomeType as DomainIncomeTypeValue,
} from "@porchpass/api-client";
import {
  commaSeparatedNumbersToArray,
  createIsoDate,
  mapZeroDate,
  parseIsoDate,
  pick,
  zeroDate,
} from "src/utils";
import { requiredField } from "./required";

export type IncomeFormData = {
  id?: string;
  income_type?: DomainIncomeTypeValue;
  amount?: string;
  children_ages?: string;
  income_type_other?: string;
  start_date?: string;
  from_month?: string;
  from_year?: string;
  end_date?: string;
  to_month?: string;
  to_year?: string;
  // Form-only state for the Ongoing switch; not sent to the API
  is_ongoing?: boolean;
};

export type IncomeListFormData = {
  incomes: Partial<IncomeFormData>[];
};

const EMPLOYMENT_INCOME_TYPES: string[] = [
  DomainIncomeType.IncomeBase,
  DomainIncomeType.IncomeBonus,
  DomainIncomeType.IncomeCommission,
  DomainIncomeType.IncomeOvertime,
];

export function fromIncomesList(
  data: DtoGetBorrowerIncomesResp,
): IncomeListFormData {
  const mappedIncomes = (data.incomes ?? [])
    .map((income) => mapIncomeToForm(income))
    .filter(
      ({ income_type }) => !EMPLOYMENT_INCOME_TYPES.includes(income_type ?? ""),
    );

  return { incomes: mappedIncomes };
}

function mapIncomeToForm(income: DtoIncome): Partial<IncomeFormData> {
  const mappedData: Partial<IncomeFormData> = {
    ...income,
    amount: String(income.amount),
    start_date: mapZeroDate(income.start_date),
    end_date: mapZeroDate(income.end_date),
    children_ages: income.children_ages?.join(","),
  };

  if (income.start_date && income.start_date !== zeroDate) {
    const { month, year } = parseIsoDate(income.start_date);
    mappedData.from_year = year;
    mappedData.from_month = month;
  }

  if (income.end_date && income.end_date !== zeroDate) {
    const { month, year } = parseIsoDate(income.end_date);
    mappedData.to_year = year;
    mappedData.to_month = month;
  }

  return mappedData;
}

export function toCreateIncomeReq(data: IncomeFormData): DtoCreateIncomeReq {
  const mappedData: DtoCreateIncomeReq = {
    amount: parseInt(requiredField(data.amount, "amount")),
    children_ages: commaSeparatedNumbersToArray(data.children_ages),
    frequency: DomainFrequencyType.FrequencyMonthly,
    income_type: data.income_type,
    income_type_other: data.income_type_other,
  };

  if (data.from_month && data.from_year) {
    mappedData.start_date = createIsoDate(
      parseInt(data.from_year),
      parseInt(data.from_month),
    );
  }

  if (data.to_year && data.to_month) {
    mappedData.end_date = createIsoDate(
      parseInt(data.to_year),
      parseInt(data.to_month),
    );
  }

  return mappedData;
}

export function toUpdateIncomeReq(data: IncomeFormData): DtoUpdateIncomeReq {
  const mappedData: DtoUpdateIncomeReq = {
    ...pick(data, ["income_type", "start_date", "end_date"]),
    amount: parseInt(requiredField(data.amount, "amount")),
    children_ages: commaSeparatedNumbersToArray(data.children_ages),
    frequency: DomainFrequencyType.FrequencyMonthly,
    income_type: requiredField(data.income_type, "income_type"),
    income_type_other: data.income_type_other,
  };

  if (data.from_month && data.from_year) {
    mappedData.start_date = createIsoDate(
      parseInt(data.from_year),
      parseInt(data.from_month),
    );
  }

  if (data.to_year && data.to_month) {
    mappedData.end_date = createIsoDate(
      parseInt(data.to_year),
      parseInt(data.to_month),
    );
  } else if (data.end_date) {
    mappedData.end_date = zeroDate;
  }

  return mappedData;
}
